import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApplicationService } from 'src/app/core/services/application.service';
import { AUDIT_COMPONENT_NAME, AUDIT_DETAILS } from './audit-context';

/**
 * HTTP interceptor that attaches an `audit-context` header to every outgoing
 * API request. The header value is a JSON object containing:
 *
 * - `component_name` — the front-end component that initiated the request
 *   (set per-request via the `x-audit-component-name` header)
 * - `details` — a human-readable description of the request purpose
 *   (set per-request via the `x-audit-details` header)
 * - `centre_location_id` — the currently selected centre location ID
 *   (automatically pulled from ApplicationService)
 *
 * The interceptor reads the temporary `x-audit-*` headers, combines them with
 * the `centre_location_id`, produces a single `audit-context` JSON header,
 * and strips the temporary headers before the request is sent to the server.
 *
 * ### Usage in feature services
 *
 * ```ts
 * import { AUDIT_COMPONENT_NAME, AUDIT_DETAILS } from 'src/app/core/interceptors/audit-context';
 *
 * // When calling GlobalDataStore methods that accept headers:
 * this.splitBillingService.createRecord(data, [], {
 *   [AUDIT_COMPONENT_NAME]: 'SplitBillingComponent',
 *   [AUDIT_DETAILS]: 'Creating billing contact for child',
 * });
 * ```
 *
 * The `centre_location_id` is always included automatically — no need to set
 * it per-request.
 */
@Injectable()
export class AuditContextInterceptor implements HttpInterceptor {
  constructor(private appService: ApplicationService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const componentName = request.headers.get(AUDIT_COMPONENT_NAME) || '';
    const details = request.headers.get(AUDIT_DETAILS) || '';
    const centreLocationId = this.appService.app.centerLocation?.id;

    const auditContext = JSON.stringify({
      component_name: componentName,
      details: details,
      centre_location_id: centreLocationId ?? '',
    });

    // Clone the request: add the audit-context header and strip the temporary
    // x-audit-* headers so they are never sent to the backend server.
    const clonedRequest = request.clone({
      setHeaders: {
        'audit-context': auditContext,
      },
    });

    // Remove the temporary audit headers that were used for transport
    const cleanedRequest = clonedRequest.clone({
      headers: clonedRequest.headers
        .delete(AUDIT_COMPONENT_NAME)
        .delete(AUDIT_DETAILS),
    });

    return next.handle(cleanedRequest);
  }
}
